import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Stack,
  Divider
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';

/**
 * 通用页面 Header 组件
 * 
 * @param {Object} props
 * @param {string} props.title - 页面标题
 * @param {string|function} props.backPath - 返回路径，或返回函数。默认 '/dashboard'
 * @param {ReactNode} props.icon - 左侧图标（可选）
 * @param {ReactNode} props.centerContent - 中间内容（可选，如 Chip、标签等）
 * @param {ReactNode} props.actions - 右侧操作按钮（可选）
 * @param {boolean} props.showBackButton - 是否显示返回按钮，默认 true
 * @param {string} props.position - AppBar position，默认 'static'
 * @param {Object} props.sx - 额外的样式
 */
function PageHeader({
  title,
  backPath = null,
  onBack,
  icon,
  centerContent,
  actions,
  showBackButton = true,
  position = 'static',
  sx = {}
}) {
  const navigate = useNavigate();

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else if (typeof backPath === 'function') {
      backPath();
    } else if (backPath) {
      navigate(backPath);
    } else {
      // 默认返回上一页
      navigate(-1);
    }
  };

  return (
    <AppBar 
      position={position} 
      color="default" 
      elevation={0} 
      sx={{ 
        borderBottom: 1, 
        borderColor: 'divider', 
        bgcolor: 'background.paper',
        ...sx 
      }}
    >
      <Toolbar sx={{ px: { xs: 1, sm: 2 }, py: 1.5, gap: 2, justifyContent: 'space-between' }}>
        {/* Left Side - Back Button, Icon, Title */}
        <Stack 
          direction="row" 
          spacing={2} 
          alignItems="center" 
          sx={{ 
            flex: { xs: '1 1 auto', md: '0 0 auto' },
            minWidth: 0,
            maxWidth: { xs: 'calc(50% - 80px)', md: 'none' }
          }}
        >
          {showBackButton && (
            <>
              <IconButton 
                type="button"
                edge="start" 
                size="small" 
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleBack();
                }}
                sx={{ 
                  flexShrink: 0,
                  color: 'text.secondary',
                  minWidth: 44,
                  minHeight: 44,
                  '&:hover': { bgcolor: 'action.hover' },
                  position: 'relative',
                  zIndex: 1
                }}
              >
                <ArrowBack />
              </IconButton>
              <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />
            </>
          )}
          
          {icon && (
            <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
              {icon}
            </Box>
          )}
          
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 600,
              fontSize: { xs: '0.95rem', sm: '1.1rem' },
              overflow: 'hidden', 
              textOverflow: 'ellipsis', 
              whiteSpace: 'nowrap',
              color: 'text.primary'
            }}
          >
            {title}
          </Typography>
          
          {centerContent && (
            <>
              <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />
              <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                {centerContent}
              </Box>
            </>
          )}
        </Stack>

        {/* Right Side - Actions */}
        {actions && (
          <Stack 
            direction="row" 
            spacing={1.5} 
            alignItems="center" 
            sx={{ 
              flex: { xs: '0 0 auto', md: '0 0 auto' }
            }}
          >
            {actions}
          </Stack>
        )}
      </Toolbar>
    </AppBar>
  );
}

export default PageHeader;

